import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Rocket, Trash2, Lightbulb } from "lucide-react";
import { fmtINR, VENTURE_STAGES } from "./bizdevUtils";

/** Iter 169 · Ventures registry — business lines with revenue targets. */

const BLANK = { name: "", tagline: "", description: "", stage: "concept",
                  business_model: "", color: "#f97316",
                  target_monthly_revenue: 0, current_monthly_revenue: 0 };

export default function VenturesTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/bizdev/ventures`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load ventures")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = { ...editing, target_monthly_revenue: Number(editing.target_monthly_revenue) || 0,
                     current_monthly_revenue: Number(editing.current_monthly_revenue) || 0 };
    try {
      if (editing.id) {
        await api.patch(`/workspaces/${wsId}/bizdev/ventures/${editing.id}`, body);
      } else {
        await api.post(`/workspaces/${wsId}/bizdev/ventures`, body);
      }
      toast.success("Venture saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const archive = async (v) => {
    if (!window.confirm(`Archive venture "${v.name}"? Its ideas become standalone.`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/bizdev/ventures/${v.id}`);
      toast.success("Venture archived");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Archive failed")); }
  };

  return (
    <div className="space-y-3" data-testid="bizdev-ventures-tab">
      <div className="flex items-center">
        <div className="text-xs text-muted-foreground">
          Every business line you're running or dreaming up — with a revenue target to keep you honest.
        </div>
        <div className="ml-auto">
          <Button size="sm" className="h-8 text-xs"
                  onClick={() => setEditing({ ...BLANK })}
                  data-testid="ventures-new-btn">
            <Plus className="w-3 h-3 mr-1" /> New venture
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((v) => {
          const target = Number(v.target_monthly_revenue) || 0;
          const current = Number(v.current_monthly_revenue) || 0;
          const pct = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
          return (
            <div key={v.id} className="border border-border bg-card p-4"
                 style={{ borderTop: `3px solid ${v.color || "#f97316"}` }}
                 data-testid={`venture-card-${v.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{v.name}</div>
                  {v.tagline && <div className="text-xs text-muted-foreground truncate">{v.tagline}</div>}
                </div>
                <span className="text-[9px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">
                  {v.stage}
                </span>
              </div>
              <div className="mt-3 text-xs flex items-center justify-between">
                <span className="text-muted-foreground">MRR {fmtINR(current)} / {fmtINR(target)}</span>
                <span className="font-mono">{pct}%</span>
              </div>
              <div className="h-1.5 bg-muted mt-1 overflow-hidden rounded-full">
                <div className="h-full rounded-full"
                     style={{ width: `${pct}%`, background: v.color || "#f97316" }} />
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" /> {v.idea_count} idea{v.idea_count !== 1 ? "s" : ""}
                </span>
                {v.weighted_monthly > 0 && (
                  <span className="text-emerald-700 font-mono">+{fmtINR(v.weighted_monthly)}/mo upside</span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  <button type="button" onClick={() => setEditing({ ...v })}
                          className="text-primary hover:underline"
                          data-testid={`venture-edit-${v.id}`}>Edit</button>
                  <button type="button" onClick={() => archive(v)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`venture-archive-${v.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="col-span-full border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            <Rocket className="w-6 h-6 mx-auto mb-2" />
            No ventures yet. Register your first business line.
          </div>
        )}
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="max-w-md" data-testid="venture-dialog">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Edit venture" : "New venture"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2.5 text-sm">
              <Input value={editing.name} placeholder="Venture name"
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                     data-testid="venture-name-input" />
              <Input value={editing.tagline || ""} placeholder="Tagline (one-liner)"
                     onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                     className="h-8 text-xs" data-testid="venture-tagline-input" />
              <textarea value={editing.description || ""}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        placeholder="What is this business? Who does it serve?"
                        className="w-full h-16 text-xs p-2 border border-border rounded" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Stage</label>
                  <Select value={editing.stage}
                          onValueChange={(v) => setEditing({ ...editing, stage: v })}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="venture-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VENTURE_STAGES.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Business model</label>
                  <Input value={editing.business_model || ""}
                         onChange={(e) => setEditing({ ...editing, business_model: e.target.value })}
                         placeholder="SaaS, agency, D2C…"
                         className="h-8 text-xs mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Target MRR ₹</label>
                  <Input type="number" min={0} value={editing.target_monthly_revenue}
                         onChange={(e) => setEditing({ ...editing, target_monthly_revenue: e.target.value })}
                         className="h-8 text-xs mt-0.5" data-testid="venture-target-input" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Current MRR ₹</label>
                  <Input type="number" min={0} value={editing.current_monthly_revenue}
                         onChange={(e) => setEditing({ ...editing, current_monthly_revenue: e.target.value })}
                         className="h-8 text-xs mt-0.5" data-testid="venture-current-input" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Accent color</label>
                <input type="color" value={editing.color || "#f97316"}
                       onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                       className="block h-8 w-16 mt-0.5 border border-border cursor-pointer" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy} data-testid="venture-save-btn">
                {busy ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
